const PendingOrientationChangeTimeoutMs = 250;
const PendingResizeTimeoutMs = 200;

/**
 * Helper class that "re-fires" an orientation change event only after we think the window has resized, which helps bridge differences
 * between how orientation change is handled on different platforms.
 * There is no guarantee that every orientation change event fired from the window will be mirrored here.
 *
 * Notes on how the eventing works in different scenarios:
 * In iOS, the order is resize -> orientation change.
 * In Android, the order is orientation change -> resize.
 * When in an iframe, either OS will propagate the orientation change event to the iframe's window. The resize only occurs if the
 * parent document happens to resize the iframe, so the order and timing of the two events will depend on how the parent document
 * responds to the event. Currently we won't fire if no resize occurs, which prevents false alarms in the iframe case.
 */
class OrientationChange {
  private static latestPendingOrientationChangeTimestamp: number | null = null;
  private static latestPendingResizeTimestamp: number | null = null;
  private static listeners: (() => void)[] = [];

  public static init(): void {
    window.addEventListener('orientationchange', OrientationChange.handleOrientationChange);
    window.addEventListener('resize', OrientationChange.handleResize);
  }

  private static handleOrientationChange(): void {
    if (OrientationChange.latestPendingResizeTimestamp !== null &&
        Date.now() - OrientationChange.latestPendingResizeTimestamp <= PendingResizeTimeoutMs) {
      /* Resize occurred recently, assume this is the iOS case and fire. */
      OrientationChange.fireOrientationChange();
    } else {
      /* No resize occurred recently, but record the timestamp for the Android case where a resize is about to occur. */
      OrientationChange.latestPendingOrientationChangeTimestamp = Date.now();
    }
  }

  private static handleResize(): void {
    if (OrientationChange.latestPendingOrientationChangeTimestamp !== null &&
        Date.now() - OrientationChange.latestPendingOrientationChangeTimestamp <= PendingOrientationChangeTimeoutMs) {
      /* Orientation change occurred recently, assume this is the Android case and fire. */
      OrientationChange.fireOrientationChange();
    } else {
      /**
       * No orientation change occurred recently, but record the timestamp for the iOS case where an orientation change is about
       * to occur.
       */
      OrientationChange.latestPendingResizeTimestamp = Date.now();
    }
  }

  /**
   * Public only for testing convenience.
   * Notify listeners that an orientation change has occurred and the window has resized already.
   */
  public static fireOrientationChange(): void {
    OrientationChange.latestPendingOrientationChangeTimestamp = null;
    OrientationChange.latestPendingResizeTimestamp = null;

    OrientationChange.listeners.forEach((callback) => { callback(); });
  }

  /**
   * Register a callback when an orientation change occurs.
   */
  public static addListener(callback: () => void): void {
    if (callback) {
      OrientationChange.listeners.push(callback);
    }
  }

  /**
   * Unregister a callback that has been previously registered.
   */
  public static removeListener(callback: () => void): void {
    OrientationChange.listeners = OrientationChange.listeners.filter((element: () => void) => {
      return element !== callback;
    });
  }
}

OrientationChange.init();

export {
  OrientationChange,
  /* Timeouts are exported for testing */
  PendingOrientationChangeTimeoutMs,
  PendingResizeTimeoutMs
};
